/**
 * Locking a period.
 *
 * The checklist is the point. Locking freezes the figures and hands them to
 * the next period as its prior column, so the dialog restates what is being
 * frozen — how many numbers were found, how many a person typed, that no gap
 * is still open, and how every covenant test came out — before it will do it.
 *
 * The covenant lines are one per OUTCOME, worst first, and each names its
 * tests. A single "N pass" line is true and still hides the breach: Copperline
 * Energy locked at a fixed-charge coverage of 0.86x against a 1.15x covenant
 * and the dialog said "1 covenant pass · near threshold: …" on the way. A
 * breach does not block the lock — it is a fact to be recorded, not a gap to
 * be filled — so it has to be impossible to miss instead.
 */

import { AlertTriangle, CircleCheck } from "lucide-react";
import React, { useId, useMemo, useRef, useState } from "react";

import ModalDialog from "../ModalDialog";
import type {
	MonitoringCovenantOutcome,
	MonitoringCovenantResult,
	MonitoringLockPayload,
	MonitoringRun,
} from "../../types/monitoring";

/** How a checklist line reads: a fact, a warning, or a failure. */
type LineTone = "pass" | "near" | "breach";

const LINE_TEXT: Record<LineTone, string> = {
	pass: "text-text-base",
	near: "text-[#92400e]",
	breach: "text-red-600",
};

const LINE_ICON: Record<LineTone, string> = {
	pass: "text-[#004C4B]",
	near: "text-[#92400e]",
	breach: "text-red-600",
};

export interface LockDialogProps {
	run: MonitoringRun;
	onLock: (payload: MonitoringLockPayload) => void;
	onClose: () => void;
	/** A lock request is in flight. */
	saving: boolean;
}

const LockDialog: React.FC<LockDialogProps> = ({
	run,
	onLock,
	onClose,
	saving,
}) => {
	const [note, setNote] = useState("");
	const [signoff, setSignoff] = useState(run.partner_signoff_requested);
	const titleId = useId();
	const noteId = useId();
	const signoffId = useId();
	const cancelRef = useRef<HTMLButtonElement | null>(null);

	const counts = useMemo(() => {
		const found = run.figures.filter((f) => f.state === "found").length;
		const manual = run.figures.filter((f) => f.state === "manual").length;
		return { found, manual };
	}, [run.figures]);

	/** One line per outcome that actually occurred, worst first. */
	const covenantLines = useMemo(() => {
		const withResult = (result: MonitoringCovenantOutcome) =>
			run.covenants.filter((covenant) => covenant.result === result);
		const names = (covenants: MonitoringCovenantResult[]) =>
			covenants.map((covenant) => covenant.label).join(", ");

		const breach = withResult("breach");
		const near = withResult("near");
		const pass = withResult("pass");
		const lines: Array<{ key: string; tone: LineTone; text: string }> = [];
		if (breach.length > 0) {
			lines.push({
				key: "breach",
				tone: "breach",
				text: `${breach.length} breach: ${names(breach)}`,
			});
		}
		if (near.length > 0) {
			lines.push({
				key: "near",
				tone: "near",
				text: `${near.length} near a limit: ${names(near)}`,
			});
		}
		if (pass.length > 0) {
			lines.push({ key: "pass", tone: "pass", text: `${pass.length} pass` });
		}
		return lines;
	}, [run.covenants]);

	const breaches = run.covenants.filter(
		(covenant) => covenant.result === "breach",
	).length;
	const gapsClear = run.gaps_open === 0;

	return (
		<ModalDialog titleId={titleId} initialFocusRef={cancelRef} onClose={onClose}>
			<h2 id={titleId} className="text-xl font-semibold text-[#111111]">
				Lock {run.period.label} for {run.borrower.name}
			</h2>
			<p className="mt-1.5 text-sm leading-relaxed text-[#5B6472]">
				The figures and their sources are frozen as they are now. The next period
				starts with them as its prior period, and changes after this need a new run.
			</p>
			{breaches > 0 && (
				<p className="mt-1 text-sm font-medium text-red-600">
					Locks a run with {breaches} covenant breach
					{breaches === 1 ? "" : "es"}.
				</p>
			)}

			<ul className="mt-4 flex flex-col gap-2 text-sm">
				<li className="flex items-start gap-2">
					<CircleCheck
						className="mt-0.5 h-4 w-4 shrink-0 text-[#004C4B]"
						aria-hidden="true"
					/>
					<span className="text-text-base">
						{counts.found} figure{counts.found === 1 ? "" : "s"} read from the
						package, each with its page
					</span>
				</li>
				<li className="flex items-start gap-2">
					<CircleCheck
						className="mt-0.5 h-4 w-4 shrink-0 text-[#004C4B]"
						aria-hidden="true"
					/>
					<span className="text-text-base">
						{counts.manual} entered by a person, with where it came from
					</span>
				</li>
				<li className="flex items-start gap-2">
					{gapsClear ? (
						<CircleCheck
							className="mt-0.5 h-4 w-4 shrink-0 text-[#004C4B]"
							aria-hidden="true"
						/>
					) : (
						<AlertTriangle
							className="mt-0.5 h-4 w-4 shrink-0 text-[#92400e]"
							aria-hidden="true"
						/>
					)}
					<span className={gapsClear ? "text-text-base" : "text-[#92400e]"}>
						{gapsClear
							? "No open checks"
							: `${run.gaps_open} open check${run.gaps_open === 1 ? "" : "s"}`}
					</span>
				</li>
				{covenantLines.map((line) => (
					<li
						key={line.key}
						className="flex items-start gap-2"
						data-testid={`lock-covenants-${line.key}`}
					>
						{line.tone === "pass" ? (
							<CircleCheck
								className={`mt-0.5 h-4 w-4 shrink-0 ${LINE_ICON[line.tone]}`}
								aria-hidden="true"
							/>
						) : (
							<AlertTriangle
								className={`mt-0.5 h-4 w-4 shrink-0 ${LINE_ICON[line.tone]}`}
								aria-hidden="true"
							/>
						)}
						<span className={LINE_TEXT[line.tone]}>{line.text}</span>
					</li>
				))}
			</ul>

			<label className="mt-4 flex items-center gap-2 text-sm text-text-base">
				<input
					id={signoffId}
					type="checkbox"
					checked={signoff}
					onChange={(event) => setSignoff(event.target.checked)}
					disabled={saving}
					className="rounded border-gray-300"
				/>
				Request partner sign-off
			</label>

			<label
				htmlFor={noteId}
				className="mt-3 block text-sm font-normal text-text-muted"
			>
				Note for the record (optional)
			</label>
			<input
				id={noteId}
				value={note}
				onChange={(event) => setNote(event.target.value)}
				disabled={saving}
				className="mt-1 h-10 w-full rounded-lg border border-[#d1d5db] px-3 text-sm text-[#111111] focus:border-[#004C4B] focus:outline-none disabled:bg-gray-100"
			/>

			<div className="mt-4 flex justify-end gap-2">
				<button
					ref={cancelRef}
					type="button"
					onClick={onClose}
					disabled={saving}
					className="inline-flex h-10 items-center rounded-[10px] border border-gray-200 px-4 text-sm font-semibold text-[#1F2937] hover:bg-gray-50 disabled:opacity-50"
				>
					Cancel
				</button>
				{/* A breach does NOT disable this. An open gap is missing work; a
				    breached covenant is a finding, and the lock is how the period
				    it was found in gets recorded. */}
				<button
					type="button"
					onClick={() =>
						onLock({
							note: note.trim() ? note.trim() : undefined,
							partner_signoff_requested: signoff,
						})
					}
					disabled={saving || !gapsClear}
					className="inline-flex h-10 items-center rounded-[10px] bg-[#004C4B] px-4 text-sm font-semibold text-white hover:bg-[#003a3a] disabled:opacity-50"
				>
					Lock {run.period.label}
				</button>
			</div>
		</ModalDialog>
	);
};

export default LockDialog;
